import React, { useState } from "react";
import axios from "axios";
import { Download, FileImage, Check, Mail } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROMO_PREVIEW =
  "https://customer-assets.emergentagent.com/job_indigenous-bush-maps/artifacts/4di8xxg1_Facebook%20Post%20940x788%20px%20-%20Poster%20A3%20%281%29.webp";

const BULLETS = [
  "What a Bush Map is — at a glance",
  "Our six-stage process visualised",
  "How Bush Maps support funding and planning",
  "Six reasons to choose Indigenous-led mapping",
];

export default function DownloadPromo() {
  const [form, setForm] = useState({ name: "", organisation: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // { download_url, resource_title }

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/downloads`, {
        ...form,
        resource: "bush-maps-overview",
      });
      setDone(data);
      toast.success("Thank you — your download is ready.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d?.msg || "Invalid input").join(", ")
        : typeof detail === "string"
        ? detail
        : "Could not process your request. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="download"
      data-testid="section-download"
      className="relative py-24 md:py-32 bg-[#EBE4D8] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left — preview */}
          <div className="lg:col-span-7">
            <div className="bm-eyebrow mb-6">Free Resource</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2D2621]">
              The Bush Maps <br className="hidden md:block" /> Overview Brochure
            </h2>
            <p className="mt-6 text-[#584D45] text-lg leading-relaxed max-w-xl">
              A single-page visual introduction to Bush Mapping — perfect to
              share with your board, funders or community before a Discovery
              Call.
            </p>

            <div className="relative mt-10 group">
              <div className="relative overflow-hidden border border-[#C5B6A3] bg-[#FDFBF7] shadow-xl">
                <img
                  src={PROMO_PREVIEW}
                  alt="Bush Maps Overview preview"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  className="block w-full h-auto"
                  style={{ userSelect: "none" }}
                />
              </div>
            </div>
          </div>

          {/* Right — form / success */}
          <div className="lg:col-span-5">
            <div className="bg-[#FDFBF7] border border-[#C5B6A3] p-8 md:p-10 lg:sticky lg:top-28">
              {!done ? (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <span className="w-12 h-12 rounded-full bg-[#A95A3F] text-white flex items-center justify-center">
                      <FileImage size={20} />
                    </span>
                    <div>
                      <div className="font-serif-display text-2xl text-[#2D2621] leading-tight">
                        Get the brochure
                      </div>
                      <div className="text-xs text-[#7A6C63] tracking-wide">
                        Instant access · no spam
                      </div>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-7">
                    {BULLETS.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-[#2D2621] text-[14.5px] leading-snug"
                      >
                        <Check size={14} className="mt-1 text-[#A95A3F] shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>

                  <form onSubmit={submit} className="space-y-5" data-testid="download-form">
                    <label className="block">
                      <span className="block text-[10px] tracking-[0.24em] uppercase text-[#7A6C63] mb-1">
                        Your name *
                      </span>
                      <input
                        data-testid="download-name"
                        className="bm-input"
                        placeholder="Full name"
                        value={form.name}
                        onChange={update("name")}
                        required
                      />
                    </label>
                    <label className="block">
                      <span className="block text-[10px] tracking-[0.24em] uppercase text-[#7A6C63] mb-1">
                        Organisation
                      </span>
                      <input
                        data-testid="download-org"
                        className="bm-input"
                        placeholder="Ranger group, school, agency…"
                        value={form.organisation}
                        onChange={update("organisation")}
                      />
                    </label>
                    <label className="block">
                      <span className="block text-[10px] tracking-[0.24em] uppercase text-[#7A6C63] mb-1">
                        Email *
                      </span>
                      <input
                        data-testid="download-email"
                        type="email"
                        className="bm-input"
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={update("email")}
                        required
                      />
                    </label>

                    <button
                      type="submit"
                      data-testid="download-submit"
                      disabled={loading}
                      className="bm-btn-primary w-full justify-center disabled:opacity-60"
                    >
                      {loading ? "Sending…" : "Send me the brochure"}{" "}
                      {!loading && <Download size={16} />}
                    </button>

                    <p className="text-[11px] text-[#7A6C63] leading-relaxed">
                      We respect your inbox. Your email is shared only with
                      William Martin and used to send you the brochure.
                    </p>
                  </form>
                </>
              ) : (
                <div data-testid="download-success">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="w-12 h-12 rounded-full bg-[#2C3C2B] text-[#E9C7A8] flex items-center justify-center">
                      <Check size={22} />
                    </span>
                    <div>
                      <div className="font-serif-display text-2xl text-[#2D2621] leading-tight">
                        Ready to download
                      </div>
                      <div className="text-xs text-[#7A6C63] tracking-wide">
                        We&apos;ve also emailed you a copy
                      </div>
                    </div>
                  </div>

                  <p className="text-[#584D45] leading-relaxed mb-6">
                    Thanks. The Bush Maps Overview is ready below — and a copy
                    has been sent to your inbox.
                  </p>

                  <a
                    href={done.download_url}
                    download
                    target="_blank"
                    rel="noreferrer"
                    data-testid="download-link"
                    className="bm-btn-primary w-full justify-center"
                  >
                    Download the brochure <Download size={16} />
                  </a>

                  <div className="mt-6 pt-6 border-t border-[#E2DACF] text-sm text-[#7A6C63] flex items-center gap-2">
                    <Mail size={14} />
                    Sent to {form.email}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
